import { zodResolver } from '@hookform/resolvers/zod'
import { Grid } from '@mui/joy'
import { useStore } from 'effector-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Region, WorkRule, VehicleCategory, CarModel } from '~/shared/api'
import {
  fuelTypeEnumOptions,
  Option,
  OptionEnum,
  transmissionEnumOptions,
} from '~/shared/config/constants'
import { FuelTypeEnum, TransmissionEnum } from '~/shared/config/enums'
import {
  AsyncAutocompleteInput,
  AutocompleteInput,
  DatePickerInput,
  FormButtons,
  formButtonsModel,
  FormProvider,
  NumericInput,
  Switch,
  TextInput,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'
import { useGeneratedTitle } from './useGeneratedTitle'

const formSchema = WorkRule.schema.pick({
  title: true,
  activeFrom: true,
  transmissionType: true,
  pricePerDay: true,
  manufactureYears: true,
  regionsIds: true,
  vehicleCategoryId: true,
  carModelOption: true,
  fuelTypes: true,
  isPromotion: true,
})

export type SchemaType = z.infer<typeof formSchema>

export type FormValues = Omit<
  SchemaType,
  | 'regionsIds'
  | 'vehicleCategoryId'
  | 'carModelOption'
  | 'fuelTypes'
  | 'transmissionType'
  | 'manufactureYears'
> & {
  regionsIds?: UniqueId[] | Option[]
  vehicleCategoryId: UniqueId | Option | null
  carModelOption: UniqueId | Option | null
  fuelTypes: OptionEnum<FuelTypeEnum>[] | FuelTypeEnum[]
  transmissionType: OptionEnum<TransmissionEnum> | TransmissionEnum | null
  manufactureYears: string | number[]
}

export type FormProps = {
  isCreating?: boolean
  canEdit?: boolean
  isSubmitting?: boolean
  isLoading?: boolean
  onSuccess?: (formValues: FormValues) => void
  defaultValues?: Partial<FormValues>
  onTitleChange?: (title: string) => void
}

export function Form({
  isCreating,
  canEdit,
  isSubmitting,
  isLoading,
  defaultValues,
  onSuccess,
  onTitleChange,
}: FormProps) {
  const { t } = useTranslation()

  const isEditing = useStore(formButtonsModel.$isEditing)
  const disabled = (!isEditing && !isCreating) || isSubmitting

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  useEffect(() => {
    if (defaultValues) form.reset(defaultValues)
    // eslint-disable-next-line
  }, [defaultValues, isEditing])

  useGeneratedTitle(form, onTitleChange)

  return (
    <FormProvider form={form} onSuccess={onSuccess} canEdit={canEdit}>
      <Container>
        <ContainerTitle>{t('basic information')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={6}>
            <AsyncAutocompleteInput
              label='form.vehicle category'
              name='vehicleCategoryId'
              placeholder='form.select'
              fetchOptions={VehicleCategory.fetchOptions}
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid xs={6}>
            <TextInput
              label='form.year of manufacture'
              name='manufactureYears'
              placeholder='form.enter year of manufacture separated by commas'
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid xs={6}>
            <AutocompleteInput
              label='form.transmission type'
              name='transmissionType'
              placeholder='form.select'
              options={transmissionEnumOptions}
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid xs={6}>
            <DatePickerInput
              label='form.active from'
              name='activeFrom'
              placeholder='form.select date'
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid xs={12}>
            <AsyncAutocompleteInput
              label='form.car model'
              name='carModelOption'
              placeholder='form.add car model'
              fetchOptions={CarModel.fetchOptions}
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid container xs={12} alignItems='flex-end'>
            <Grid container xs={6} data-testid='container-fuel-types'>
              <AutocompleteInput
                label='form.fuel type'
                name='fuelTypes'
                placeholder='form.add fuel type'
                options={fuelTypeEnumOptions}
                skeletonShow={isLoading}
                readOnly={disabled}
                multiple
                autocompleteXs={12}
                autocompleteBoxSx={{ padding: 0 }}
                dataTestId='Вид топлива'
              />
            </Grid>
            <Grid container xs={6}>
              <Grid xs={6}>
                <NumericInput
                  label='form.rental cost per day'
                  name='pricePerDay'
                  placeholder='form.enter rental cost per day'
                  skeletonShow={isLoading}
                  readOnly={disabled}
                />
              </Grid>

              <Grid xs={6}>
                <Switch
                  labelI18n='form.car is in promotion'
                  name='isPromotion'
                  formControlProps={{ sx: { marginTop: '40px' } }}
                  skeletonShow={isLoading}
                  readOnly={disabled}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>

      <Container data-testid='container-regions'>
        <ContainerTitle>{t('form.regions')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <AsyncAutocompleteInput
            name='regionsIds'
            placeholder='form.add region'
            fetchOptions={Region.fetchOptions}
            skeletonShow={isLoading}
            readOnly={disabled}
            multiple
            autocompleteXs={6}
            dataTestId='Регионы'
          />
        </Grid>
      </Container>

      <FormButtons loading={isSubmitting} isCreating={isCreating} />
    </FormProvider>
  )
}
